import React, { useState } from 'react';
import { theme } from '../theme';
import { getApiUrl } from '../config';
import { useAuth } from '../contexts/AuthContext';

const DISMISS_KEY = 'emailVerifyBannerDismissedAt';
const DISMISS_HOURS = 24;

const EmailVerifyBanner: React.FC = () => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.emailVerified) return null;

  // Esconder por 24h si el user descartó el banner
  const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!hidden && dismissedAt && Date.now() - dismissedAt < DISMISS_HOURS * 60 * 60 * 1000) {
    return null;
  }
  if (hidden) return null;

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl('/api/auth/verify-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo enviar el email');
      }
      setResent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error de conexión');
    } finally {
      setResending(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setHidden(true);
  };

  return (
    <div
      style={{
        margin: '12px 16px 0',
        padding: '12px 16px',
        background: 'rgba(255, 167, 38, 0.12)',
        border: '1px solid rgba(255, 167, 38, 0.4)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <span style={{ fontSize: 22, lineHeight: 1 }}>✉️</span>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, marginBottom: 2 }}>
          {resent ? 'Te reenviamos el email' : 'Verificá tu email'}
        </div>
        <div style={{ fontSize: 12.5, color: theme.colors.textSecondary, lineHeight: 1.4 }}>
          {resent
            ? `Revisá ${user.email}. Si no llega en unos minutos, revisá la carpeta de spam.`
            : `Te mandamos un link a ${user.email} cuando creaste tu cuenta.`}
        </div>
        {error && (
          <div style={{ fontSize: 12, color: theme.colors.danger, marginTop: 4 }}>{error}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {!resent && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            style={{
              padding: '8px 14px',
              background: '#FFA726',
              color: '#181A1B',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: resending ? 'not-allowed' : 'pointer',
              opacity: resending ? 0.7 : 1,
            }}
          >
            {resending ? 'Enviando…' : 'Reenviar'}
          </button>
        )}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Cerrar"
          style={{
            padding: '8px 10px',
            background: 'transparent',
            color: theme.colors.textSecondary,
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default EmailVerifyBanner;
